import React, { useEffect, useState, useCallback } from "react";
import api, { toErrMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import DupGroupResolve from "@/components/DupGroupResolve";
import { fmtDateTime } from "@/lib/dt";
import { asArray } from "@/lib/asArray";
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)

/**
 * Round 11 · Multi-file Phase 2 — M1/M2/M3 shared panel.
 *
 * Owner mandate: same R8 pattern as Inventory count / Completeness —
 * files list, per-file soft delete + restore, combined totals,
 * duplicate flag + resolve, EN + AR — but driven over the generic
 * ``/attachments`` + ``/multi-file-attachments`` endpoints so each
 * worksheet dialog (Estimates / Related-party / Lead-sheet) can mount
 * one line.
 *
 * Props:
 *   engId        — current engagement id.
 *   contextType  — one of "estimate" | "related_party" | "lead_sheet".
 *   contextId    — the worksheet ref (Estimates: ``ref``, RP: ``ref``,
 *                  Lead-sheet: ``{code}-02``).
 *   testid       — stable prefix for data-testids ("est-mfa",
 *                  "rp-mfa", "lead-mfa").
 */
export default function MultiFileAttachmentsPanel({ engId, contextType,
                                                        contextId, testid }) {
  const { t } = useI18n();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const prefix = testid || `mfa-${contextType}`;

  const load = useCallback(async () => {
    if (!engId || !contextId) return;
    try {
      const r = await api.get(`/multi-file-attachments/${engId}/${contextType}/${encodeURIComponent(contextId)}`);
      setState(r.data);
    } catch (e) { toast.error(toErrMsg(e, "Load failed")); }
  }, [engId, contextType, contextId]);

  useEffect(() => { load(); }, [load]);

  // Batch 9 · A4: several files in one go (like the form "Supporting documents") — one request per file, one reload
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    let ok = 0;
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("engagement_id", engId);
        fd.append("context_type",  contextType);
        fd.append("context_id",    contextId);
        try {
          await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
          ok += 1;
        } catch (err) { toast.error(`${f.name}: ${toErrMsg(err, "Upload failed")}`); }
      }
      if (ok) toast.success(files.length > 1 ? `${ok} / ${files.length} ${t("mfa.filesAdded") || "files added"}` : (t("mfa.uploadOk") || "File added"));
      await load();
    } finally { setBusy(false); e.target.value = ""; }
  };

  const remove = async (attId) => {
    if (!await askConfirm(t("mfa.removeConfirm")
          || "Remove this file? It will go to the recycle bin.")) return;
    try {
      await api.delete(`/attachments/${attId}`);
      toast.success(t("mfa.removedOk") || "File moved to recycle bin");
      await load();
    } catch (e) { toast.error(toErrMsg(e, "Remove failed")); }
  };

  if (!state) {
    return <div className="rounded border p-2 text-xs text-muted-foreground"
                data-testid={`${prefix}-loading`}>{t("common.loading")}</div>;
  }

  const files    = state.files || [];
  const unres    = Number(state.unresolved_duplicates || 0);
  const kb       = (b) => `${(Number(b || 0) / 1024).toFixed(1)} KB`;

  return (
    <section className="rounded border p-2 text-xs" data-testid={prefix}>
      <div className="mb-1 flex items-center justify-between">
        <div className="font-semibold">
          {t("mfa.title") || "Supporting files"}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground"
             data-testid={`${prefix}-summary`}>
          <span><b>{state.total_count}</b>{" "}
            {t("inv6.filesLabel") || "file(s)"}</span>
          <span className="font-mono">{kb(state.total_bytes)}</span>
        </div>
      </div>

      <div className="mb-1 flex flex-wrap items-center gap-2">
        <Button asChild size="sm" variant="outline">
          <label htmlFor={`${prefix}-upload`} className="cursor-pointer"
                 data-testid={`${prefix}-upload-button`}>
            {busy ? (t("common.saving") || "Uploading…")
                   : (t("common.chooseFiles") || t("common.chooseFile") || "Choose files")}
          </label>
        </Button>
        <input id={`${prefix}-upload`} type="file" multiple
               accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls"
               className="hidden" onChange={upload}
               data-testid={`${prefix}-upload-input`} />
      </div>

      {unres > 0 && (
        <div className="mb-1 rounded border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900"
             data-testid={`${prefix}-dup-banner`}>
          <b>{unres}</b>{" "}
          {t("mfa.dupBanner") || "duplicate filename(s) across files"} ·
          <span dir="rtl">تكرارات في اسم الملف بين الملفات</span>
        </div>
      )}
      {asArray(state.duplicate_groups).map((g) => (
        <DupGroupResolve key={g.key} group={g}
                           engId={engId}
                           resolveUrl={`/multi-file-attachments/${engId}/${contextType}/${encodeURIComponent(contextId)}/duplicates/${encodeURIComponent(g.key)}/resolve`}
                           onResolved={load} />
      ))}

      {files.length === 0 ? (
        <div className="rounded border p-2 text-center text-[11px] text-muted-foreground"
             data-testid={`${prefix}-empty`}>—</div>
      ) : (
        <ul className="divide-y rounded border bg-white dark:bg-slate-900"
            data-testid={`${prefix}-list`}>
          {files.map((f) => (
            <li key={f.id} className="flex items-center justify-between gap-2 px-2 py-1 text-[11px]"
                data-testid={`${prefix}-row-${f.id}`}>
              <div className="min-w-0 flex-1 break-words">
                <a href={`/api/attachments/${f.id}/download`}
                   className="font-mono underline"
                   data-testid={`${prefix}-dl-${f.id}`}>{f.filename}</a>
                <span className="ms-2 text-muted-foreground">
                  {kb(f.size)} · {fmtDateTime(f.uploaded_at)}
                  {f.uploaded_by_name ? ` · ${f.uploaded_by_name}` : ""}
                </span>
              </div>
              <Button size="sm" variant="ghost" className="h-6 text-[11px] text-red-700"
                      data-testid={`${prefix}-remove-${f.id}`}
                      onClick={() => remove(f.id)}>
                {t("common.remove") || "Remove"}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
